import React from 'react'

import { useThresholdConfiguration } from '../hooks/useThresholdConfiguration'

import { Box, GridItem, HStack, useDisclosure } from '@chakra-ui/react'

import {
  Button,
  FormNumberInputControl,
  SkeletonModalBody,
} from '@/components/ui'

import { EditDeviceModalSection } from '../../components/EditDeviceModalSection'
import { SectionHeader } from '../../components/SectionHeader'
import { TwoColumnGrid } from '../../components/TwoColumnGrid'
import { ResetThresholdConfirmationModal } from './ResetThresholdConfirmationModal'

interface ThresholdConfigurationSectionIProps {
  deviceId: string
  onSaveCallback?: () => void
}

const MIN_THRESHOLD = 0
const MAX_THRESHOLD = 30

export const ThresholdConfigurationSection = ({
  deviceId,
  onSaveCallback,
}: ThresholdConfigurationSectionIProps) => {
  const {
    isOpen: isResetConfirmOpen,
    onOpen: onResetConfirmOpen,
    onClose: onResetConfirmClose,
  } = useDisclosure()
  const {
    accessGrantedThreshold,
    doorCloseThreshold,
    handleAccessGrantedChange,
    handleDoorCloseChange,
    isDefault,
    isDeleteLoading,
    isLoading,
    isResetDisabled,
    isSaveDisabled,
    isUpsertLoading,
    onResetToDefaults,
    onSave,
  } = useThresholdConfiguration(deviceId)

  const handleResetConfirm = async () => {
    await onResetToDefaults()
    onResetConfirmClose()
  }

  if (isLoading) {
    return <SkeletonModalBody />
  }

  return (
    <EditDeviceModalSection mb={6}>
      <TwoColumnGrid>
        <GridItem>
          <SectionHeader
            subTitle={`Configure thresholds for AI Operator auto-resolution of door incidents. Threshold\u00A0range\u00A0(${MIN_THRESHOLD}-${MAX_THRESHOLD}s)`}
            title='AI Operator Thresholds'
          />
        </GridItem>
        <GridItem justifySelf='end'>
          <HStack spacing={3}>
            <Button
              data-testid='thresholdConfig_resetButton'
              isDisabled={isResetDisabled}
              onClick={onResetConfirmOpen}
              variant='secondary'
            >
              Reset
            </Button>
            <Button
              data-testid='thresholdConfig_saveButton'
              isDisabled={isSaveDisabled}
              isLoading={isUpsertLoading}
              loadingText='Saving'
              onClick={async () => {
                await onSave()
                onSaveCallback?.()
              }}
            >
              Save
            </Button>
          </HStack>
        </GridItem>
      </TwoColumnGrid>

      <Box>
        <HStack spacing={6}>
          <FormNumberInputControl
            id='accessGrantedTimeThreshold'
            label='Access Granted Threshold (seconds)'
            labelOpacity={0.5}
            max={MAX_THRESHOLD}
            min={MIN_THRESHOLD}
            onChange={handleAccessGrantedChange}
            size='md'
            value={accessGrantedThreshold}
            width='250px'
          />
          <FormNumberInputControl
            id='doorCloseTimeThreshold'
            label='Door Closed Threshold (seconds)'
            labelOpacity={0.5}
            max={MAX_THRESHOLD}
            min={MIN_THRESHOLD}
            onChange={handleDoorCloseChange}
            size='md'
            value={doorCloseThreshold}
            width='250px'
          />
        </HStack>
      </Box>

      {isDefault && (
        <Box color='gray.500' fontSize='12px' mt={4}>
          Currently using application defaults. Save custom values to override.
        </Box>
      )}

      <ResetThresholdConfirmationModal
        isLoading={isDeleteLoading}
        isOpen={isResetConfirmOpen}
        onClose={onResetConfirmClose}
        onConfirm={handleResetConfirm}
      />
    </EditDeviceModalSection>
  )
}
